import React, { useMemo } from 'react'
import { useTar, TranslationKey } from '@/modules/i18n'
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    MarkdownEditor,
    Card,
    CardContent,
    IconTooltip,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '@duck/ui'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { GradeBadge } from '../../GradeBadge/GradeBadge'
import { WysiwygEditor } from '@components/WysiwygEditor'
import { ChallengeService, PartnerService } from '@duck/api-definitions'
import { getContentChartIds } from '@components/WysiwygEditor/wysiwygUtils'
import { cn } from '@duck/ui'
import { DevPrint } from '@components/DevPrint'
import { DownloadPDFButton } from '@/components/DownloadPDFButton/DownloadPDFButton'

export interface FeedbackDetails {
    personName: string
    feedback: string
    startDate?: string
    submitDate?: string
    grade?: number
    solution?: string
    workTime?: number
    charts?: ChallengeService.WorkState['charts']
    queries?: PartnerService.AssignmentDetailsQueries
    challengeId?: string
}

const formatWorkTime = (workTimeMinutes: number): string => {
    if (!workTimeMinutes) return '0m'
    const hours = Math.floor(workTimeMinutes / 60)
    const minutes = workTimeMinutes % 60
    if (hours > 0) {
        return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
}

const QueriesTabContent: React.FC<{ queries: PartnerService.AssignmentDetailsQueries }> = ({ queries }) => {
    const { t } = useTar()
    const total = queries.totalCount ?? queries.last20?.length ?? 0
    const entries = queries.last20 ?? []

    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
                {t('challenges.queriesTotal' as TranslationKey, { count: total })}
            </p>
            <ul className="space-y-3 list-none p-0 m-0">
                {entries.map((entry, index) => (
                    <li
                        key={index}
                        className="border border-border rounded-md overflow-hidden bg-muted/10"
                    >
                        <div className="p-3">
                            <pre
                                className="bg-card text-xs font-mono text-foreground whitespace-pre-wrap break-all overflow-x-auto max-h-32 overflow-y-auto custom-scrollbar"
                                title={entry.query}
                            >
                                {entry.query}
                            </pre>
                            {(entry.resultCount !== undefined || entry.errorCode) && (
                                <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                    {entry.resultCount !== undefined && (
                                        <span>{t('challenges.queryResultCount' as TranslationKey, { count: entry.resultCount })}</span>
                                    )}
                                    {entry.errorCode && (
                                        <span className="text-destructive">{t('challenges.queryError' as TranslationKey)}: {entry.errorCode}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}

interface ChallengeSummaryDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    feedbackDetails: FeedbackDetails | null
}

export const ChallengeSummaryDialog: React.FC<ChallengeSummaryDialogProps> = ({
    open,
    onOpenChange,
    feedbackDetails,
}) => {
    const { t } = useTar()
    // Extract chart IDs from solution HTML and filter charts to only those in the solution
    const solutionCharts = useMemo(() => {
        if (!feedbackDetails?.solution || !feedbackDetails?.charts) {
            return {}
        }

        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = feedbackDetails.solution
        const chartIds = getContentChartIds(tempDiv)

        // Filter charts to only include those that are in the solution
        const filteredCharts: ChallengeService.WorkState['charts'] = {}
        chartIds.forEach(chartId => {
            if (feedbackDetails.charts && feedbackDetails.charts[chartId]) {
                filteredCharts[chartId] = feedbackDetails.charts[chartId]
            }
        })

        return filteredCharts
    }, [feedbackDetails?.solution, feedbackDetails?.charts])

    if (!feedbackDetails) return null
    const hasQueries = !!feedbackDetails.queries?.last20?.length

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl overflow-hidden" noMinHeight>
                <DialogHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-col min-w-0 gap-2">
                            <DialogTitle>{t('challenges.submissionSummary')}</DialogTitle>
                            <p className="text-sm text-muted-foreground blur-sm">{feedbackDetails.personName}</p>
                            <DevPrint label="challengeId" value={feedbackDetails.challengeId} />
                        </div>
                        {feedbackDetails.challengeId && (
                            <DownloadPDFButton
                                challengeId={feedbackDetails.challengeId}
                                disabled={!feedbackDetails.feedback}
                            />
                        )}
                    </div>
                </DialogHeader>
                <div className="space-y-3 overflow-y-auto pe-2 custom-scrollbar mb-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{t('common.started')}</p>
                                    {feedbackDetails.startDate ? (
                                        <RelativeDateTime
                                            isoDateString={feedbackDetails.startDate}
                                            className="text-sm font-medium"
                                        />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{t('common.submitted')}</p>
                                    {feedbackDetails.submitDate ? (
                                        <RelativeDateTime
                                            isoDateString={feedbackDetails.submitDate}
                                            className="text-sm font-medium"
                                        />
                                    ) : (
                                        <p className="text-sm text-muted-foreground">-</p>
                                    )}
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1 ">
                                        {t('common.actualWorkTime')}
                                        <IconTooltip content={t('common.actualWorkTimeTooltip')} />
                                    </div>
                                    <p className="text-sm font-medium">
                                        {feedbackDetails.workTime !== undefined ? formatWorkTime(feedbackDetails.workTime) : '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">{t('common.grade')}</p>
                                    <div className="flex items-center">
                                        {feedbackDetails.grade !== undefined ? (
                                            <GradeBadge grade={feedbackDetails.grade} small />
                                        ) : (
                                            <p className="text-sm text-muted-foreground">-</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Tabs defaultValue="feedback" className="w-full">
                        <TabsList className={cn("grid w-full grid-cols-3", hasQueries ? 'grid-cols-3' : 'grid-cols-2')}>
                            <TabsTrigger value="feedback" disabled={!feedbackDetails.feedback}>
                                {t('challenges.aiFeedback')}
                            </TabsTrigger>
                            <TabsTrigger value="solution" disabled={!feedbackDetails.solution}>
                                {t('challenges.submittedSolution')}
                            </TabsTrigger>
                            {hasQueries && <TabsTrigger value="queries">
                                {t('challenges.queries' as TranslationKey)}
                            </TabsTrigger>}
                        </TabsList>
                        <TabsContent value="solution" className="mt-3">
                            {feedbackDetails.solution && (
                                <div className="border border-border rounded-md p-4">
                                    <WysiwygEditor
                                        content={feedbackDetails.solution}
                                        startingCharts={solutionCharts}
                                        readOnly
                                    />
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="feedback" className="mt-3">
                            {feedbackDetails.feedback && (
                                <MarkdownEditor
                                    defaultValue={feedbackDetails.feedback}
                                    className="w-full h-full"
                                    mode="view"
                                />
                            )}
                        </TabsContent>
                        <TabsContent value="queries" className="mt-3">
                            {feedbackDetails.queries && (
                                <QueriesTabContent queries={feedbackDetails.queries} />
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>
                        {t('common.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
